import { XIcon } from '@heroicons/react/outline';
import { Drawer, Input, Progress } from 'antd';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { handleEmailOnBlur } from '../SignUpModal';
import { calculatePasswordStrength } from '../../Auth/SignUp';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import EmailIcon from '../../../../assets/Icons/Mail';
import PasswordIcon from '../../../../assets/Icons/password';
import CustomIcon from '../../../../assets/Icons/User';
import { authModalDrawerController } from '../../../atoms/authModalDrawerController';
import { user } from '../../../atoms/user';
import { useAuth } from '../../../hooks/useAuth';
import { User } from '../../../utils/interfaces/User';

interface ModalComponentProps {
  visible: boolean;
  onCancel: () => void;
  isRegister?: boolean;
  noRedirect?: boolean;
  isClosable?: boolean;
}

const SignUpDrawer: React.FC<ModalComponentProps> = ({
  visible,
  onCancel,
  isRegister = false,
  isClosable = true,
}) => {
  const [, setVisibleDrawer] = useRecoilState(authModalDrawerController);

  const userData: User | undefined = useRecoilValue(user);
  const { onSignUp, loading } = useAuth();

  const handleOk = () => {
    setTimeout(() => {
      handleClose();
    }, 500);
  };
  const [passwordStrength, setPasswordStrength] = useState<number>(0);

  const [formData, setFormData] = useState({
    fullname: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    retryPassword: '',
  });

  const [errors, setErrors] = useState({
    fullname: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    retryPassword: '',
  });

  const handleClose = () => {
    setErrors({
      fullname: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      retryPassword: '',
    });
    setFormData({
      fullname: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      retryPassword: '',
    });
    onCancel();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
    if (name === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const validationErrors = {
      fullname: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      retryPassword: '',
    };
    if (!formData.email) {
      validationErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      validationErrors.email = 'Invalid email';
    }
    if (formData.email) {
      const res = await handleEmailOnBlur(formData.email);
      validationErrors.email = res;
    }
    if (!formData.fullname) {
      validationErrors.fullname = 'Full Name is required';
    }
    // if (!formData.phone) {
    //   validationErrors.phone = 'Phone number is required';
    // } else if (!isValidPhone(formData.phone)) {
    //   validationErrors.phone = 'Enter Valid 10 digit number';
    // }
    if (!formData.password) {
      validationErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      validationErrors.password = 'Password must be at least 8 characters long';
    }
    // if (!formData.username) {
    //   validationErrors.username = 'Username is required';
    // }

    setErrors(validationErrors);

    if (Object.values(validationErrors).every((error) => !error)) {
      const signUpData = {
        email: formData.email,
        password: formData.password,
        name: formData.fullname,
        phone: formData.phone,
        isRegister: isRegister,
        noRedirect: true,
        ...(isRegister && { registerClose: handleClose }),
      };

      await onSignUp(signUpData);
      handleOk();
    }
  };

  const getStrengthText = () => {
    switch (passwordStrength) {
      case 1:
        return 'Too Weak';
      case 2:
        return 'Weak';
      case 3:
        return 'Moderate';
      case 4:
        return 'Strong';
      case 5:
        return 'Very Strong';
      default:
        return '';
    }
  };
  const isValidEmail = (email: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (userData && isRegister) {
      setFormData({
        ...formData,
        email: userData?.email || '',
        fullname: userData?.name || '',
        phone: userData?.phone || '',
      });
    }
  }, [userData]);

  return (
    <Drawer
      placement='bottom'
      open={visible}
      height='65%'
      // onOk={handleOk}
      closable={false}
      // confirmLoading={confirmLoading}
      onClose={onCancel}
      footer={false}
      className='customLoginModal bg-bgColor bg-opacity-70 h-full relative rounded-2xl border-2 border-borderDarkGray backdrop-filter backdrop-blur-md '
    >
      <div className='flex justify-center'>
        <div className='w-full  md:w-4/5 lg:w-3/5 xl:w-6/12  2xl:w-5/12'>
          <div className='flex items-center gap-y-10 flex-col px-4 scroll-px-0.5  md:px-10 justify-center'>
            <div className={` z-30  flex flex-col gap-y-1 w-full pb-5`}>
              <div className={clsx('py-3 flex flex-col gap-y-2 relative ')}>
                <h1 className='text-xl flex justify-center gap-x-2 font-medium lg:font-normal lg:text-2xl mt-1 leading-10 text-fadedWhite text-center w-full'>
                  {isRegister ? 'Register' : 'Create account'} for{' '}
                  <p className='text-primary font-medium'>Free</p>
                </h1>
                <span className='text-base lg:text-base text-textDarkGrayColor text-center flex justify-center'>
                  No cost what-so-ever to{' '}
                  {isRegister ? 'register' : 'create account.'}
                </span>
                <ShouldRender check={isClosable}>
                  <span
                    onClick={() => {
                      handleClose();
                    }}
                    className='absolute right-0 top-[30%] cursor-pointer'
                  >
                    <XIcon className='w-4 h-4 md:w-6 md:h-6 text-fadedWhite' />
                  </span>
                </ShouldRender>
              </div>

              <form
                onSubmit={handleSubmit}
                className='flex flex-col mt-2 gap-y-3'
              >
                <div className='col-span-1 flex-col flex'>
                  <Input
                    name='fullname'
                    value={formData.fullname}
                    placeholder='Full Name'
                    prefix={
                      <span className='pr-2'>
                        <CustomIcon filled={true} />
                      </span>
                    }
                    onChange={handleChange}
                    style={{
                      backgroundColor: '#000000',
                    }}
                    size='large'
                    className='border text-sm focus-within:bg-bgColor  placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray  py-3  text-white bg-bgColor customInputBlack makeBgBlack'
                  />
                  {errors.fullname && (
                    <p className='text-red-500'>{errors.fullname}</p>
                  )}
                </div>
                <div className='col-span-1 flex-col flex gap-y-.5'>
                  <Input
                    type='email'
                    name='email'
                    autoComplete='current-email'
                    autoFocus={false}
                    value={formData.email}
                    style={{
                      backgroundColor: '#000000',
                    }}
                    placeholder='Email'
                    prefix={
                      <span className='pr-2'>
                        <EmailIcon filled={true} />
                      </span>
                    }
                    onChange={handleChange}
                    size='large'
                    className='border text-sm focus-within:bg-bgColor  placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray  py-3  text-white bg-bgColor customInputBlack makeBgBlack'
                  />
                  {errors.email && (
                    <p className='text-red-500'>{errors.email}</p>
                  )}
                </div>
                <div className='col-span-1 flex-col flex '>
                  <Input.Password
                    type='password'
                    name='password'
                    placeholder='Password'
                    autoComplete='current-password'
                    value={formData.password}
                    style={{
                      backgroundColor: '#000000',
                    }}
                    prefix={
                      <span className='pr-2'>
                        <PasswordIcon />
                      </span>
                    }
                    onChange={handleChange}
                    size='large'
                    className='border text-sm focus-within:bg-bgColor  placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray  py-3  text-white bg-bgColor customInputBlack makeBgBlack'
                  />
                  {errors.password && (
                    <p className='text-red-500'>{errors.password}</p>
                  )}
                  <ShouldRender check={formData.password}>
                    {passwordStrength !== null && (
                      <span className='relative'>
                        <Progress
                          percent={(passwordStrength / 5) * 100}
                          showInfo={false}
                          status={
                            passwordStrength >= 3 ? 'success' : 'exception'
                          }
                        />
                        <p
                          className={`mt-2 text-${
                            passwordStrength >= 3 ? 'green' : 'red'
                          }-500 absolute -bottom-2.5`}
                        >
                          {getStrengthText()}
                        </p>
                      </span>
                    )}
                  </ShouldRender>
                </div>

                <span className='flex justify-center mt-2'>
                  <ButtonDefault
                    size={4}
                    variant={ButtonVariants.WHITE}
                    className='p-1 w-full'
                    disabled={loading}
                    loading={loading}
                    rounded={true}
                    enableScaling={false}
                  >
                    <span className='flex text-sm font-bold gap-x-2 px-4 py-1 whitespace-nowrap'>
                      {isRegister ? 'Register' : 'Sign up'}
                    </span>
                  </ButtonDefault>
                </span>

                <span className='text-center font-medium flex text-white justify-center text-sm pt-4'>
                  Already have an account?
                  <p
                    onClick={() => {
                      setVisibleDrawer((prevState) => ({
                        ...prevState,
                        login: true,
                        signUp: false,
                      }));
                      if (isRegister) {
                        handleClose();
                      }
                    }}
                    className='text-primary  px-1 hover:font-bold hover:underline cursor-pointer transition-all duration-300 ease-in-out'
                  >
                    Log in
                  </p>
                </span>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SignUpDrawer;
